import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Typography,
  Stack,
  Box,
  Paper,
  Tabs,
  Tab,
  Chip,
  Card,
  Drawer,
  Checkbox,
} from '@mui/material';
import SnackbarInfo from '../common/snackbar-info.component';
import Iconify from '../common/iconify.component';
import { LISTING_COLUMNS } from '../../config/module-configs/manage-videos.config';
import ListData from '../table-elements/list-data.component';
import { getListData } from '../../services/videos.service';
import {
  addVideosById,
  removeVideosById,
} from '../../services/distributor.service';
import { ACTIONS } from '../../config/const.config';
import FilterVideos from '../filter-videos/filter-video.component';
import { FiltersContext } from '../../context/filter.context';
import DistributorFilterVideos from '../filter-videos/distributor-filter-videos.component';
import useAccess from '../../hooks/access.hook';
import ConfirmPopup from '../common/confirm-popup.component';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ContentOwnerVideoAssignment = ({ distributorDetail }) => {
  const { getSignal, isAbortError } = useAbortableRequest();

  const { hasContentOwnerAccess } = useAccess();

  const defaultValueRef = useRef([]);
  const { handleToggleFilters } = useContext(FiltersContext);
  const [isActionTriggered, setIsActionTriggered] = useState(false);

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    columns: LISTING_COLUMNS,
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {},
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [activeAction, setActiveAction] = useState('view');

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleVideosListRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    const counter =
      Object.values(selectedFilters).filter((filter) => filter.apply).length ||
      0;

    setOptions({
      ...options,
      appliedFilters: { ...selectedFilters },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleClearFilters = () => {
    setOptions({
      ...options,
      appliedFilters: {},
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }

    const updatedFilters = { ...options.appliedFilters };
    if (field === 'licenese_expired_videos') {
      updatedFilters[field] = { ...updatedFilters[field], value: false };
    } else {
      delete updatedFilters[field];

      updatedFilters[field] = {
        ...updatedFilters[field],
        value: Array.isArray(updatedFilters[field]?.value) ? [] : null || '',
      };
    }

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleGlobalSelection = () => {
    setSelectAllPages(!selectAllPages);
    setSelectedRows([]);
  };

  const handleDeleteData = () => {
    setIsActionTriggered(true);
    setOptions({
      ...options,
      loading: true,
    });

    const filters = Object.entries(options.appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));

    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const removePayload = {
      videos: selectedRows,
      isAllSelected: selectAllPages,
      id: distributorDetail.id,
      filter: filters,
    };

    removeVideosById(distributorDetail.id, removePayload)
      .then(() => {
        setIsActionTriggered(false);
        handleVideosListRefreshData();
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Selected videos removed successfully.',
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setIsActionTriggered(false);
      });
    handleActionCancel();
  };

  const handleAddData = () => {
    setIsActionTriggered(true);
    setOptions({
      ...options,
      loading: true,
    });
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const filters = Object.entries(options.appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));

    const addPayload = {
      videos: selectedRows,
      isAllSelected: selectAllPages,
      id: distributorDetail.id,
      filter: filters,
    };

    addVideosById(distributorDetail.id, addPayload)
      .then(() => {
        setIsActionTriggered(false);
        handleVideosListRefreshData();

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Selected videos added successfully.',
        });
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setIsActionTriggered(false);
      });
    handleActionCancel();
  };

  useEffect(() => {
    const signal = getSignal();
    setSelectAllPages(false);
    setSelectedRows([]);
    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = Object.entries(options.appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));

    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const columns = LISTING_COLUMNS;

    const uri = '';
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(
      uri + paramsQuery,
      {
        id: distributorDetail.id,
        videoIds: defaultValueRef.current,
        from: 'distributorVideoAssignment',
        action: activeAction,
        filter: filters,
      },
      signal
    )
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            columns,
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [activeAction, options.reloadCounter]);

  return (
    <>
      <Box
        display="flex"
        width="calc(100% - 250px)"
        component={Paper}
        padding={2}
      >
        <Box flex={1} width="calc(100% - 250px)">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 0, py: 2 }}
          >
            <Tabs
              value={activeAction}
              aria-label="tabs"
              indicatorColor="primary"
              onChange={(_, newValue) => {
                setOptions({
                  ...options,
                  sortBy: '',
                  sortOrder: '',
                  page: 0,
                  reloadCounter: options.reloadCounter + 1,
                  loading: true,
                });
                setActiveAction(newValue);
              }}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="view" label="Existing Assignment" />
              <Tab value="add" label="Assign More Videos" />
              <Tab value="remove" label="Remove Videos from Assignment" />
            </Tabs>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 0, pb: 1 }}
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                sx={{
                  padding: 1,
                }}
                checked={selectAllPages}
                onChange={handleGlobalSelection}
                disabled={options.loading || !options.rows.length}
              />
              <Typography variant="body2" color="text.secondary">
                Select All
              </Typography>
            </Box>

            <Box display="flex" gap={1}>
              {activeAction !== 'view' && (
                <Button
                  color="primary"
                  variant="contained"
                  disabled={
                    !(selectedRows.length || selectAllPages) ||
                    options.loading ||
                    isActionTriggered
                  }
                  startIcon={
                    <Iconify
                      icon={
                        activeAction === 'add'
                          ? 'ic:round-add'
                          : 'ic:round-remove'
                      }
                    />
                  }
                  onClick={
                    activeAction === 'add'
                      ? handleAddData
                      : () => handleActionSelect(ACTIONS.DELETE.value)
                  }
                >
                  {activeAction === 'add' ? 'Add Videos' : 'Remove Videos'}
                </Button>
              )}

              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleVideosListRefreshData}
                disabled={options.loading}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          {options.appliedFilters &&
            Object.values(options.appliedFilters).some(
              (filter) =>
                filter.apply &&
                ((filter.type === 'checkbox' && filter.value) ||
                  filter.type !== 'checkbox')
            ) && (
              <Box mb={2}>
                <Typography variant="body1" fontWeight="bold" display="inline">
                  Filters Applied:{' '}
                  <Chip
                    key="all"
                    label="Clear all"
                    onDelete={() => handleDeleteFilter('clear-all')}
                    sx={{ ml: 1, color: 'red' }}
                  />
                </Typography>
                {Object.entries(options.appliedFilters)
                  .filter(
                    ([, filter]) =>
                      filter.apply &&
                      ((filter.type === 'checkbox' && filter.value) ||
                        filter.type !== 'checkbox')
                  )
                  .map(([field, filter]) => (
                    <Chip
                      key={field}
                      label={filter.title || 'Active'}
                      onDelete={() => handleDeleteFilter(field)}
                      sx={{ ml: 1 }}
                    />
                  ))}
              </Box>
            )}

          <Card>
            <ListData
              columns={options.columns}
              rows={options.rows}
              page={options.page}
              rowsPerPage={options.rowsPerPage}
              totalRows={options.totalRows}
              loading={options.loading}
              actions={[]}
              error={options.error}
              sortBy={options.sortBy}
              sortOrder={options.sortOrder}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onSortChange={handleSortingChange}
              onAction={handleActionSelect}
              showCheckbox={activeAction !== 'view'}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              selectAllPages={selectAllPages}
              setSelectAllPages={setSelectAllPages}
            />
          </Card>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open
        variant="persistent"
        PaperProps={{
          sx: {
            width: 265,
            overflow: 'auto',
            height: '89.5%',
            top: '10.8%',
            right: 0,
          },
        }}
      >
        {hasContentOwnerAccess() ? (
          <DistributorFilterVideos
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
            from="video-assignment"
          />
        ) : (
          <FilterVideos
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
            from="video-assignment"
          />
        )}
      </Drawer>

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Remove Selected videos"
          message={`Are you sure you want to remove ${
            selectAllPages ? options.totalRows : selectedRows.length
          } videos? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
          successButtonText="Proceed"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="remove"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ContentOwnerVideoAssignment.propTypes = {
  distributorDetail: PropTypes.object.isRequired,
};

export default ContentOwnerVideoAssignment;
